import AxiosInstance, { AxiosInstanceType } from '../../core/api/axiosinstance'
import { VerificationStatus } from '../../shared/enum'
import { promiseToResult, querystring } from '../../utils'
import { Content } from './_type'

export interface Passport {
  id: number
  name: string
  surname: string
  birthDate: string
  verificationStatus: VerificationStatus | null
}

export interface User {
  id: number
  role: string
  phone: string
  login: string | null
  avatar: string | null
  language: string
  createdAt: string
  passport: Passport | null
}

export interface My extends User {
  isImported: boolean
  hasPassword: boolean
  isBlocked: boolean
  isWithdrawalEnabled: boolean
  email: string | null
  socials: {
    whatsApp: boolean
    telegram: boolean
    sms: boolean
    email: string
  }
  projects: {
    project: {
      id: number
      name: string
      symbol: string
    }
    shares: number
  }[]
  balance: number
  parent: User | null
  referralsCount: number
  supervisory: boolean
}

export interface Verification {
  id: number
  comment: string
  status: VerificationStatus
  createdAt: string
}

export interface Referral extends User {
  depth: number
  parent: number
  isRestricted: boolean
  referrals: number
}

export interface StoriesSteps {
  url: Content[] // string
  type: 'image' | 'video'
  duration: number // ms
  header?: {
    heading: Content[] // string
    subheading: Content[] // string
    profileImage: string // url
  },
  more?: {
    body?: Content[] // html
    url?: Content[] // string
  }
}

export interface Stories {
  id: number
  adminName: string
  url: Content[] // preview story
  like: number
  view: number
  steps: StoriesSteps[]
}

export type GetMyRes = My
const getMy = () => {
  return promiseToResult(AxiosInstance().get<GetMyRes>(
    '/application/users/@my',
  ))
}

export interface PatchMyReq {
  lang?: string
  phone?: string
  avatar?: string
  email?: string
}
export type PatchMyRes = GetMyRes
const patchMy = (data: PatchMyReq) => {
  return promiseToResult(AxiosInstance().patch<PatchMyRes>(
    '/application/users/@my',
    data,
  ))
}

export interface PostMyEmailReq {
  email: string
  redirectUrl: string
}
const postMyEmail = (data: PostMyEmailReq) => {
  data.email = data.email.toLowerCase()
  return promiseToResult(AxiosInstance().post(
    '/application/users/@my/email/verify',
    data,
  ))
}

export type PostPassportReq = Omit<Passport, 'id' | 'verificationStatus' >
export interface PostPassportRes extends Passport {
  lastVerification: Verification | null
}
const postPassport = (data: PostPassportReq) => {
  return promiseToResult(AxiosInstance().post<PostPassportRes>(
    '/application/users/@my/passport',
    data,
  ))
}

export type PatchPassportReq = PostPassportReq
export type PatchPassportRes = PostPassportRes
const patchPassport = (data: PatchPassportReq) => {
  return promiseToResult(AxiosInstance().patch<PatchPassportRes>(
    '/application/users/@my/passport',
    data,
  ))
}

export interface PostVerificationReq {
  file: File
}
export type PostVerificationRes = Verification
const postVerification = (data: PostVerificationReq) => {
  const { file } = data

  const body = new FormData()
  body.append('file', file)
  return promiseToResult(AxiosInstance(AxiosInstanceType.FORMDATA).post<PostVerificationRes>(
    '/application/users/@my/passport/verify',
    body,
  ))
}

export type GetPassportRes = PostPassportRes
const getPassport = () => {
  return promiseToResult(AxiosInstance().get<GetPassportRes>(
    '/application/users/@my/passport',
  ))
}

export interface GetStatsRes {
  transactions: {
    referralPayments: number
    withdrawalAvailable: number
    installmentsActive: number
  }
}
const getStats = () => {
  return promiseToResult(AxiosInstance().get<GetStatsRes>(
    '/application/users/@my/stats',
  ))
}

export interface GetReferralsReq {
  userId: number
}
export interface GetReferralsRes {
  metadata: {
    total: number
  }
  items: Referral[]
}
const getReferrals = ({ userId }: GetReferralsReq) => {
  return promiseToResult(AxiosInstance().get<GetReferralsRes>(
    `/application/users/referrals/preview?userId=${userId}`,
  ))
}

export type ReferralStatisticsLevel = 1 | 2 | 3 | 'other'
export interface ReferralStatistics {
  level: ReferralStatisticsLevel
  transactionSumPercentage: number
  transactionSumPrice: number
  type: string
}
export interface GetReferralStatisticsReq {
  dateStart: string
  dateEnd: string
}
export type GetReferralStatisticsRes = ReferralStatistics[]
const getReferralStatistics = ({ dateStart, dateEnd }: GetReferralStatisticsReq) => {
  return promiseToResult(AxiosInstance().get<GetReferralStatisticsRes>(
    `projects/referrals/@my/statistics?dateStart=${dateStart}&dateEnd=${dateEnd}`,
  ))
}

export interface GetUsersPreviewsReq {
  ids: number[]
  limit: number
  page: number
}
export interface GetUsersPreviewsRes {
  metadata: {
    total: number
    limit: number
    page: number
  }
  items: Referral[]
}
const getUsersPreviews = (data: GetUsersPreviewsReq) => {
  return promiseToResult(AxiosInstance().get<GetUsersPreviewsRes>(
    `/application/users/previews?${querystring.stringify(data)}`,
  ))
}

export interface GetGeoStatsRes {
  userCount: number
  latitude: string
  longitude: string
  haveTransactions: boolean
}
const getUsersGeoStats = () => {
  return promiseToResult(AxiosInstance().get<GetGeoStatsRes[]>(
    '/application/users/geo-stats',
  ))
}

const getUsersGeoStatsPublic = () => {
  return promiseToResult(AxiosInstance().get<GetGeoStatsRes[]>(
    '/application/users/geo-stats/public',
  ))
}

export interface GetUsersByGeoReq {
  latitude: string
  longitude: string
  limit: number
  page: number
}
export interface GetUsersByGeoRes {
  items: {
    userId: number
    name: string
    surname: string
    login: string | null
    avatar: string | null
  }[]
  metadata: {
    total: number
    limit: number
    page: number
  }
}

const getUsersByGeo = (data: GetUsersByGeoReq) => {
  return promiseToResult(AxiosInstance().get<GetUsersByGeoRes>(
    `/application/users/investors-by-geo?${querystring.stringify(data)}`,
  ))
}

export interface GetSearchReq {
  query: string
  limit: number
  page: number
}
export interface GetSearchRes {
  items: Referral[]
  metadata: {
    total: number
    limit: number
    page: number
  }
}
const getSearch = (data: GetSearchReq) => {
  return promiseToResult(AxiosInstance().get<GetSearchRes>(
    `/application/users/search?${querystring.stringify(data)}`,
  ))
}

export interface PutParentReq {
  id: number
}
export type PutParentRes = Referral
const putParent = (data: PutParentReq) => {
  return promiseToResult(AxiosInstance().put<PutParentRes>(
    '/application/users/@my/parent',
    data,
  ))
}

export interface PatchPasswordReq {
  code?: string
  oldPassword?: string
  newPassword: string
}
export interface PatchPasswordRes {
  status: boolean
}
const patchPassword = (data: PatchPasswordReq) => {
  return promiseToResult(AxiosInstance().patch<PatchPasswordRes>(
    '/application/users/@my/password',
    data,
  ))
}

export type GetImportUserLoginReq = string
export type GetImportUserLoginRes = User
const getImportUserLogin = (login: GetImportUserLoginReq) => {
  return promiseToResult(AxiosInstance().get<GetImportUserLoginRes>(
    `/application/users/previews-import-login?login=${login}`,
  ))
}

export interface GetAuthRes {
  metadata: {
    total: number
    limit: number
    page: number
  }
  items: {
    id: number
    ip: string
    userAgent: string
    timezone: string
    city: string
    country: string
    createdAt: string
  }[]
}

const getAuth = () => {
  return promiseToResult(AxiosInstance().get<GetAuthRes>(
    '/application/users/@my/auth-history?page=1&limit=1000',
  ))
}

export interface PutReLoginDeviceReq {
  ip: string
  userAgent: string
}
const putReLoginDevice = (data: PutReLoginDeviceReq) => {
  return promiseToResult(AxiosInstance().put(
    '/application/users/@my/relogin-device',
    data,
  ))
}

const putReLoginAllDevice = () => {
  return promiseToResult(AxiosInstance().put(
    '/application/users/@my/relogin-all-devices',
  ))
}

export interface PostInviteLinkRes {
  inviteLinks: string[]
}
const postInviteLink = () => {
  return promiseToResult(
    AxiosInstance().post<PostInviteLinkRes>(
      '/application/invite-link',
    ),
  )
}

export type GetMyStoriesRes = Stories[]
const getMyStories = () => {
  return promiseToResult(
    AxiosInstance().get<GetMyStoriesRes>(
      '/application/users/@my/stories',
    ),
  )
}

export interface PostFcmTokenReq {
  fcmToken: string
}
const postFcmToken = (data: PostFcmTokenReq) => {
  return promiseToResult(
    AxiosInstance().post<GetMyStoriesRes>(
      '/application/users/@my/register-fcm-token',
      data,
    ),
  )
}

const deleteFcmToken = (data: PostFcmTokenReq) => {
  return promiseToResult(
    AxiosInstance().post(
      '/application/users/@my/delete-fcm-token',
      data,
    ),
  )
}

export const UserAPI = {
  getMy,
  patchMy,
  postMyEmail,
  postPassport,
  patchPassport,
  postVerification,
  getPassport,
  getStats,
  getReferrals,
  getUsersGeoStats,
  getUsersGeoStatsPublic,
  getUsersByGeo,
  getReferralStatistics,
  getSearch,
  putParent,
  getUsersPreviews,
  patchPassword,
  getImportUserLogin,
  getAuth,
  putReLoginDevice,
  putReLoginAllDevice,
  postInviteLink,
  getMyStories,
  postFcmToken,
  deleteFcmToken,
}
